/**
 * ImagePreview Component - Story 6.3: Create Add Product Form
 * Individual image preview component with remove functionality
 */

import { useState } from 'react'
import { X, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Image as ImageType } from '../../types/productForm.types'
import { cn } from '@/lib/utils'

export interface ImagePreviewProps {
  image: ImageType
  index: number
  onRemove: (index: number) => void
  disabled?: boolean
  isUploading?: boolean
}

/**
 * ImagePreview Component
 * Displays a single image preview with file info and remove functionality
 *
 * @component
 * @example
 * <ImagePreview
 *   image={image}
 *   index={0}
 *   onRemove={handleRemove}
 * />
 */
export function ImagePreview({
  image,
  index,
  onRemove,
  disabled = false,
  isUploading = false
}: ImagePreviewProps) {
  const [loadError, setLoadError] = useState(false)

  return (
    <div className={cn('image-preview-container', disabled && 'opacity-50')}>
      <div className="image-preview-wrapper">
        {loadError ? (
          <div className="image-error">
            <AlertCircle size={32} />
            <p>Failed to load image</p>
          </div>
        ) : (
          <img
            src={image.preview}
            alt={`Product preview ${index + 1}`}
            className="image-preview"
            onError={() => setLoadError(true)}
          />
        )}

        {/* Remove Button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="remove-button"
          onClick={() => onRemove(index)}
          disabled={disabled || isUploading}
          aria-label={`Remove image ${index + 1}`}
        >
          <X size={20} />
        </Button>

        {/* File Name - Only show for new files (existing images don't have file info) */}
        {image.file && (
          <div className="image-info">
            <p className="image-name" title={image.file.name}>
              {image.file.name}
            </p>
            <p className="image-size">
              {(image.file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        )}

        {/* For existing images, show image ID or generic label */}
        {!image.file && image.id && (
          <div className="image-info">
            <p className="image-name">
              Existing image
            </p>
            <p className="image-size">
              Uploaded
            </p>
          </div>
        )}
      </div>

      {/* Upload Status */}
      {isUploading && (
        <div className="upload-progress">
          <div className="spinner" />
          <p>Uploading...</p>
        </div>
      )}

      {image.id && !image.file && (
        <div className="upload-success">
          <p>Uploaded</p>
        </div>
      )}
    </div>
  )
}
